<template>
  <div class="view-container">
    <div class="mode-switch">
      <span class="switch-label">邮箱模式</span>
      <button :class="['switch-btn', { active: mode === 'internal' }]" @click="mode = 'internal'"><i class="fas fa-building"></i> 内邮</button>
      <button :class="['switch-btn', { active: mode === 'external' }]" @click="mode = 'external'"><i class="fas fa-globe"></i> 外邮</button>
    </div>

    <div class="stats-row">
      <div class="stat-card" v-for="s in currentStats" :key="s.label" :style="{ borderTopColor: s.color }">
        <div class="stat-icon" :style="{ background: s.color }"><i :class="s.icon"></i></div>
        <div class="stat-info">
          <div class="stat-value">{{ s.value }}</div>
          <div class="stat-label">{{ s.label }}</div>
        </div>
      </div>
    </div>

    <div class="content-grid">
      <div class="grid-left">
        <div class="card">
          <div class="card-header"><h3><i class="fas fa-clock"></i> 最近邮件</h3></div>
          <div class="card-body no-padding">
            <div v-for="m in recentMails" :key="m.id" class="mail-item" :class="{ unread: !m.read }" @click="openMail(m)">
              <div class="mail-avatar" :style="{ background: m.avatarColor }">{{ m.avatar }}</div>
              <div class="mail-info">
                <div class="mail-top">
                  <span class="mail-from">{{ m.fromName }}</span>
                  <span class="mail-time">{{ m.time }}</span>
                </div>
                <div class="mail-subject">{{ m.subject }}</div>
                <div class="mail-preview">{{ m.preview }}</div>
              </div>
              <span v-if="!m.read" class="unread-dot"></span>
            </div>
          </div>
        </div>
      </div>
      <div class="grid-right">
        <div class="card">
          <div class="card-header"><h3><i class="fas fa-chart-pie"></i> 邮件分类统计</h3></div>
          <div class="card-body"><VChart class="chart" :option="pieChart" autoresize /></div>
        </div>
        <div class="card">
          <div class="card-header"><h3><i class="fas fa-chart-line"></i> 月度邮件趋势</h3></div>
          <div class="card-body"><VChart class="chart" :option="trendChart" autoresize /></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { use } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { PieChart, BarChart, LineChart } from 'echarts/charts'
import { TitleComponent, TooltipComponent, LegendComponent, GridComponent } from 'echarts/components'
import VChart from 'vue-echarts'
import { getMailStats, getInboxMails } from '../api/mail'

use([CanvasRenderer, PieChart, BarChart, LineChart, TitleComponent, TooltipComponent, LegendComponent, GridComponent])

const mode = ref('internal')
const loading = ref(false)

const internalStats = ref([
  { label: '未读邮件', value: 0, icon: 'fas fa-envelope', color: '#f59e0b' },
  { label: '收件箱', value: 0, icon: 'fas fa-inbox', color: '#3b82f6' },
  { label: '已发送', value: 0, icon: 'fas fa-paper-plane', color: '#10b981' },
  { label: '草稿箱', value: 0, icon: 'fas fa-edit', color: '#8b5cf6' }
])

const externalStats = ref([
  { label: '未读邮件', value: 0, icon: 'fas fa-envelope', color: '#f59e0b' },
  { label: '收件箱', value: 0, icon: 'fas fa-inbox', color: '#3b82f6' },
  { label: '已发送', value: 0, icon: 'fas fa-paper-plane', color: '#10b981' },
  { label: '垃圾邮件', value: 0, icon: 'fas fa-exclamation-triangle', color: '#ef4444' }
])

const currentStats = computed(() => mode.value === 'internal' ? internalStats.value : externalStats.value)

const recentMails = ref([])

const pieChart = ref({
  tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
  series: [{ type: 'pie', radius: ['50%','75%'], center: ['50%','50%'],
    data: [
      { value: 0, name: '收件', itemStyle: { color: '#3b82f6' } },
      { value: 0, name: '发件', itemStyle: { color: '#10b981' } },
      { value: 0, name: '草稿', itemStyle: { color: '#8b5cf6' } },
      { value: 0, name: '未读', itemStyle: { color: '#f59e0b' } }
    ],
    label: { fontSize: 10, formatter: '{b}\n{d}%' }
  }]
})

const trendChart = ref({
  tooltip: { trigger: 'axis' },
  grid: { left: 45, right: 20, top: 20, bottom: 30 },
  xAxis: { type: 'category', data: ['1月','2月','3月','4月','5月','6月'], axisLabel: { fontSize: 10 } },
  yAxis: { type: 'value', axisLabel: { fontSize: 10 } },
  series: [
    { name: '接收', type: 'bar', data: [45, 52, 68, 55, 78, 62], itemStyle: { color: '#3b82f6', borderRadius: [3,3,0,0] } },
    { name: '发送', type: 'bar', data: [38, 42, 55, 48, 62, 47], itemStyle: { color: '#10b981', borderRadius: [3,3,0,0] } }
  ]
})

/**
 * 加载邮件统计数据
 */
const loadMailStats = async () => {
  try {
    loading.value = true
    const response = await getMailStats()
    
    if (response.data) {
      const { unreadCount, inboxCount, sentCount, draftCount } = response.data
      
      // 更新内部邮件统计
      internalStats.value[0].value = unreadCount || 0
      internalStats.value[1].value = inboxCount || 0
      internalStats.value[2].value = sentCount || 0
      internalStats.value[3].value = draftCount || 0
      
      // 更新饼图数据
      pieChart.value.series[0].data = [
        { value: inboxCount || 0, name: '收件', itemStyle: { color: '#3b82f6' } },
        { value: sentCount || 0, name: '发件', itemStyle: { color: '#10b981' } },
        { value: draftCount || 0, name: '草稿', itemStyle: { color: '#8b5cf6' } },
        { value: unreadCount || 0, name: '未读', itemStyle: { color: '#f59e0b' } }
      ]
    }
  } catch (error) {
    console.error('加载邮件统计失败:', error)
  } finally {
    loading.value = false
  }
}

/**
 * 加载最近邮件
 */
const loadRecentMails = async () => {
  try {
    const response = await getInboxMails({ pageIndex: 1, pageSize: 5 })
    
    if (response.data && response.data.mails) {
      recentMails.value = response.data.mails.map(mail => ({
        id: mail.Id,
        from: mail.FromAccount,
        fromName: mail.FromName || mail.FromAccount,
        avatar: getAvatar(mail.FromName || mail.FromAccount),
        avatarColor: getRandomColor(),
        subject: mail.Titles || '(无主题)',
        preview: getPreview(mail.Contents),
        time: formatTime(mail.CreateTime),
        read: mail.IsRead === 1
      }))
    }
  } catch (error) {
    console.error('加载最近邮件失败:', error)
  }
}

/**
 * 获取头像文字
 */
const getAvatar = (name) => {
  if (!name) return '?'
  return name.substring(0, 1)
}

/**
 * 获取随机颜色
 */
const getRandomColor = () => {
  const colors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#ec4899', '#06b6d4']
  return colors[Math.floor(Math.random() * colors.length)]
}

/**
 * 获取邮件预览文本
 */
const getPreview = (content) => {
  if (!content) return ''
  // 移除HTML标签
  const text = content.replace(/<[^>]*>/g, '')
  // 截取前50个字符
  return text.length > 50 ? text.substring(0, 50) + '...' : text
}

/**
 * 格式化时间
 */
const formatTime = (datetime) => {
  if (!datetime) return ''
  
  const d = new Date(datetime)
  const now = new Date()
  const diff = now.getTime() - d.getTime()
  
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  
  if (minutes < 1) return '刚刚'
  if (minutes < 60) return `${minutes}分钟前`
  if (hours < 24) return `${hours}小时前`
  if (days === 1) return '昨天'
  if (days < 7) return `${days}天前`
  
  return d.toLocaleDateString('zh-CN')
}

const openMail = (m) => { 
  m.read = true
  // TODO: 跳转到邮件详情页
  console.log('打开邮件:', m.subject)
}

onMounted(() => {
  loadMailStats()
  loadRecentMails()
})
</script>

<style scoped>
.view-container { padding: 20px; background: #f5f7fa; overflow-y: auto; max-height: calc(100vh - 130px); }
.mode-switch { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; }
.switch-label { font-size: 13px; color: #6b7280; font-weight: 500; }
.switch-btn { padding: 6px 16px; border: 1px solid #d1d5db; border-radius: 20px; background: #fff; color: #6b7280; cursor: pointer; font-size: 12px; display: flex; align-items: center; gap: 6px; transition: all 0.2s; }
.switch-btn:hover { border-color: #f59e0b; color: #f59e0b; }
.switch-btn.active { background: #f59e0b; color: #fff; border-color: #f59e0b; }

.stats-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin-bottom: 20px; }
.stat-card { background: #fff; border-radius: 8px; border: 1px solid #e8ecf1; border-top: 3px solid #f59e0b; padding: 20px; display: flex; align-items: center; gap: 16px; }
.stat-icon { width: 48px; height: 48px; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 20px; }
.stat-value { font-size: 28px; font-weight: 700; color: #1f2937; }
.stat-label { font-size: 13px; color: #6b7280; margin-top: 2px; }

.content-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
.grid-left, .grid-right { display: flex; flex-direction: column; gap: 20px; }
.card { background: #fff; border-radius: 8px; border: 1px solid #e8ecf1; overflow: hidden; }
.card-header { padding: 12px 16px; border-bottom: 1px solid #e8ecf1; background: #fafbfc; }
.card-header h3 { margin: 0; font-size: 14px; font-weight: 600; color: #374151; display: flex; align-items: center; gap: 8px; }
.card-body { padding: 12px; }
.card-body.no-padding { padding: 0; }

.mail-item { display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-bottom: 1px solid #f3f4f6; cursor: pointer; transition: background 0.15s; }
.mail-item:hover { background: #f8fafc; }
.mail-item.unread { background: #fffdf5; }
.mail-avatar { width: 40px; height: 40px; border-radius: 6px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 14px; font-weight: 600; flex-shrink: 0; }
.mail-info { flex: 1; min-width: 0; }
.mail-top { display: flex; justify-content: space-between; margin-bottom: 3px; }
.mail-from { font-size: 13px; font-weight: 600; color: #1f2937; }
.mail-time { font-size: 11px; color: #9ca3af; }
.mail-subject { font-size: 12px; color: #374151; margin-bottom: 2px; font-weight: 500; }
.mail-preview { font-size: 11px; color: #9ca3af; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.unread-dot { width: 8px; height: 8px; border-radius: 50%; background: #f59e0b; flex-shrink: 0; }

.chart { height: 260px; }
</style>
